import {
    PipelineDefinition,
    PipelineStepInstance,
    ValidationMessage,
} from './pipelineBuilder.types'
import { getEnabledStepsInOrder, sortStepsByOrder } from './stepOrder'
import { getTransitionConfig } from './transitionConfigUtils'
import { deriveEnterBehavior } from './transitionUtils'

function sortedEnabledSteps(
    definition: PipelineDefinition,
): PipelineStepInstance[] {
    const steps = Array.isArray(definition.steps) ? definition.steps : []
    return getEnabledStepsInOrder(steps)
}

function stepIndexByType(steps: PipelineStepInstance[], type: string): number {
    return steps.findIndex((s) => s.type === type)
}

function collectAiRouteTargets(definition: PipelineDefinition): string[] {
    const targets: string[] = []
    const steps = Array.isArray(definition.steps) ? definition.steps : []
    for (const step of steps) {
        if (step.on_complete.action !== 'ai_route' || !step.on_complete.rules) {
            continue
        }
        for (const rule of step.on_complete.rules) {
            if (rule.goto) {
                targets.push(rule.goto)
            }
        }
    }
    return targets
}

export function validateDefinition(
    definition: PipelineDefinition,
): ValidationMessage[] {
    const messages: ValidationMessage[] = []
    const enabled = sortedEnabledSteps(definition)
    const steps = Array.isArray(definition.steps) ? definition.steps : []
    const stepById = new Map(steps.map((s) => [s.id, s]))

    const prospectsStep = enabled.find((s) => s.type === 'prospects')
    if (!prospectsStep) {
        messages.push({
            severity: 'error',
            message:
                'Prospects step must be enabled — every pipeline starts with the prospect pool.',
        })
    } else if (enabled[0]?.type !== 'prospects') {
        messages.push({
            severity: 'error',
            message: 'Prospects must be the first step on the bench.',
            stepId: prospectsStep.id,
        })
    }

    const leadsStep = enabled.find((s) => s.type === 'leads')
    if (!leadsStep) {
        messages.push({
            severity: 'error',
            message:
                'Leads step must be enabled — every pipeline needs an entry point.',
        })
    } else if (enabled[1]?.type !== 'leads') {
        messages.push({
            severity: 'error',
            message:
                'Leads must be the second step on the bench (after Prospects).',
            stepId: leadsStep.id,
        })
    }

    for (const targetId of collectAiRouteTargets(definition)) {
        const target = stepById.get(targetId)
        if (!target) {
            messages.push({
                severity: 'error',
                message: `AI routing references missing step "${targetId}".`,
            })
            continue
        }
        if (!target.enabled) {
            messages.push({
                severity: 'error',
                message: `AI routing references disabled step "${labelFor(
                    target,
                )}".`,
                stepId: target.id,
            })
        }
    }

    const screeningIdx = stepIndexByType(enabled, 'screening')
    const applicationIdx = stepIndexByType(enabled, 'application')
    if (
        screeningIdx >= 0 &&
        applicationIdx >= 0 &&
        screeningIdx < applicationIdx
    ) {
        messages.push({
            severity: 'warning',
            message:
                'Screening is before Application. Use a tenant-hosted screening form or capture SSN/DOB earlier.',
            stepId: enabled[screeningIdx].id,
        })
    }

    for (let i = 0; i < enabled.length - 1; i += 1) {
        const current = enabled[i]
        const next = enabled[i + 1]
        const transition = getTransitionConfig(
            current,
            next,
            definition.transition_configs,
        )
        const enter = deriveEnterBehavior(next)
        const exitGate = transition.require_pm_approval ? 'pm_review' : 'none'

        if (transition.triggers.length === 0) {
            messages.push({
                severity: 'error',
                message: `${labelFor(current)} → ${labelFor(
                    next,
                )} has no exit conditions configured.`,
                stepId: current.id,
            })
        }

        if (exitGate === 'none' && enter === 'pm_start') {
            messages.push({
                severity: 'warning',
                message: `${labelFor(
                    current,
                )} moves automatically but ${labelFor(
                    next,
                )} requires PM to start each person — people will queue until PM acts.`,
                stepId: current.id,
            })
        }
    }

    const leaseIdx = stepIndexByType(enabled, 'lease')
    const guarantorIdx = stepIndexByType(enabled, 'guarantor')
    const screeningEnabledIdx = stepIndexByType(enabled, 'screening')
    if (
        guarantorIdx >= 0 &&
        leaseIdx >= 0 &&
        guarantorIdx > leaseIdx &&
        screeningEnabledIdx < 0
    ) {
        messages.push({
            severity: 'info',
            message:
                'Guarantor is after Lease without Screening — guarantor decisions usually follow screening.',
            stepId: enabled[guarantorIdx].id,
        })
    }

    return messages
}

function labelFor(step: PipelineStepInstance): string {
    const labels: Record<string, string> = {
        prospects: 'Prospects',
        leads: 'Leads',
        tours: 'Tours',
        application: 'Application',
        screening: 'Screening',
        qualification: 'Qualification',
        lease: 'Lease',
        guarantor: 'Guarantor',
        future_resident: 'Future Resident',
        unit_assignment: 'Unit Assignment',
    }
    return labels[step.type] ?? step.type
}

export function renumberSteps(
    steps: PipelineStepInstance[],
): PipelineStepInstance[] {
    return sortStepsByOrder(steps).map((step, index) => ({
        ...step,
        order: index + 1,
    }))
}
